import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

// Interfaces
export interface ActivationCodeInputProps {
    renderInvalid: boolean;
    length?: number;
    onComplete?: (code: string) => void;
    onChange?: (code: string) => void;
    disabled?: boolean;
}

const ActivationCodeInput: React.FC<ActivationCodeInputProps> = ({
    renderInvalid,
    length = 6,
    onComplete,
    onChange,
    disabled = false,
}) => {
    const [values, setValues] = useState<string[]>(new Array(length).fill(''));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleChange = (index: number, value: string) => {
        const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

        if (sanitizedValue.length <= 1) {
            const newValues = [...values];
            newValues[index] = sanitizedValue;
            setValues(newValues);

            const code = newValues.join('');
            onChange?.(code);

            if (sanitizedValue && index < length - 1) {
                inputRefs.current[index + 1]?.focus();
            }

            if (code.length === length) {
                onComplete?.(code);
            }
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            if (!values[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
            } else {
                const newValues = [...values];
                newValues[index] = '';
                setValues(newValues);
                onChange?.(newValues.join(''));
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

        if (pasteData) {
            const newValues = new Array(length).fill('');
            for (let i = 0; i < Math.min(pasteData.length, length); i++) {
                newValues[i] = pasteData[i];
            }
            setValues(newValues);

            const code = newValues.join('');
            onChange?.(code);

            const nextEmptyIndex = newValues.findIndex(val => !val);
            const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : length - 1;
            inputRefs.current[focusIndex]?.focus();

            if (code.length === length) {
                onComplete?.(code);
            }
        }
    };

    const handleFocus = (index: number) => {
        inputRefs.current[index]?.select();
    };

    return (
        <InputContainer>
            {values.map((value, index) => (
                <CodeInput
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    value={value}
                    onChange={e => handleChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    onFocus={() => handleFocus(index)}
                    disabled={disabled}
                    maxLength={1}
                    hasValue={!!value}
                    renderInvalid={renderInvalid}
                />
            ))}
        </InputContainer>
    );
};

export default ActivationCodeInput;

const InputContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
`;

const CodeInput = styled.input<{ hasValue: boolean; disabled: boolean, renderInvalid: boolean }>`
  width: 56px;
  height: 64px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid ${props => props.renderInvalid ? 'red' : props.hasValue ? '#5ad90b'  : '#d1d5db'};
  border-radius: 8px;
  background-color: ${props =>
        props.disabled ? '#f3f4f6' :
            props.hasValue ? '#eff6ff' : 'white'
    };
  color: ${props => props.disabled ? '#9ca3af' : '#111827'};
  transition: all 0.2s ease;
  caret-color: transparent;
  outline: none;
  
  &:hover {
    border-color: ${props => !props.disabled && !props.renderInvalid && '#a2dc7e'};
  }
  
  &:focus {
    border-color: ${props => !props.renderInvalid && '#4ec206'};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

